import { useEffect, useMemo, useState } from "react";

// API
import { GetInfoCard } from "../../Api/InfoCards";

// Models
import { InfoCardDTO } from "../../Models/DTOs/InfoCardDTO";

type UseFetchInfoCardsResult = {
    cards: InfoCardDTO[];
    ordered: InfoCardDTO[];
    loading: boolean;
    error: string;
};

function stableUniqueIds(ids: number[]) {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const id of ids) {
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

// Small helper to detect a 404 from common client libs
function is404(err: any) {
    const status =
        err?.response?.status ??
        err?.status ??
        err?.statusCode ??
        err?.data?.status;
    return status === 404;
}

function useFetchInfoCardsByIds(
    infoCardIds: number[],
    refresh: number
): UseFetchInfoCardsResult {
    // Use a stable key for memoization without relying on join inside deps everywhere
    const ids = useMemo(() => stableUniqueIds(infoCardIds), [infoCardIds.join(",")]);
    const idsKey = useMemo(() => ids.join(","), [ids]);

    const [cards, setCards] = useState<InfoCardDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let isCancelled = false;
        const controller = new AbortController();

        if (!ids.length) {
            setCards([]);
            setError("");
            setLoading(false);
            return () => controller.abort();
        }

        setLoading(true);
        setError("");

        (async () => {
            // Fetch all, but don't let one failure kill the whole batch.
            const settled = await Promise.allSettled(
                ids.map((id) => GetInfoCard(id, controller))
            );

            if (isCancelled) return;

            const ok: InfoCardDTO[] = [];
            let hadNon404Error = false;

            for (let i = 0; i < settled.length; i++) {
                const r = settled[i];

                if (r.status === "fulfilled") {
                    ok.push(r.value);
                    continue;
                }

                // Rejected
                const err = r.reason;

                if (is404(err)) {
                    // Inactive/not-yet-active/not-public: ignore
                    continue;
                }

                // Anything else is a real problem
                hadNon404Error = true;
                console.error("[useFetchInfoCardsByIds] fetch failed for id", ids[i], err);
            }

            setCards(ok);

            if (hadNon404Error) {
                setError("Some info cards failed to load.");
            } else {
                setError("");
            }

            setLoading(false);
        })();

        return () => {
            isCancelled = true;
            controller.abort();
        };
    }, [idsKey, refresh]);

    // Build a map by InfoCardId so ordering works correctly
    const ordered = useMemo(() => {
        const map = new Map<number, InfoCardDTO>();
        for (const c of cards) {
            // adjust this property name if yours differs (InfoCardId vs Id)
            map.set((c as any).InfoCardId ?? (c as any).Id, c);
        }
        return ids.map((id) => map.get(id)).filter((x): x is InfoCardDTO => Boolean(x));
    }, [idsKey, cards]);

    return { cards, ordered, loading, error };
}

export default useFetchInfoCardsByIds;
